'use client';
import { useContext, type ComponentProps, type ReactNode } from 'react';
import Link from 'next/link';
import RouterProgressionContext from '@/contexts/RouterProgressionContext';

/**
 * The original Link component from Next.js no longer has router events.
 * This component is a wrapper around the Link component that adds the router events.
 * Helpful for adding a loading bar to the top of the page.
 */

type AnchorProps = Omit<ComponentProps<typeof Link>, 'href'> & {
  href: string;
  children?: ReactNode;
  onClick?: () => void;
};

export default function Anchor(props: AnchorProps): ReactNode {
  const startChange = useContext(RouterProgressionContext);

  if (props.href.startsWith('/'))
    return (
      <Link
        href={props.href}
        passHref
        onClick={() => {
          const { pathname, search, hash } = window.location;
          if (props.href !== pathname + search + hash) startChange();
          if (props.onClick) props.onClick();
        }}
        {...props}
      />
    );

  const { prefetch, ...otherProps } = props;
  return <a href={props.href} {...otherProps} />;
}
